/**
 * Canonical alert channel registry (#231).
 *
 * One typed contract shared by the Settings UI (#232), the Position Tracker
 * emit sites (#233), and the preference data layer. Every alert the product
 * emits — sound or visual — has exactly one entry here with a stable ID.
 *
 * Extension contract for #233: per-channel settings are the
 * `AlertChannelSettings` object. New optional fields (e.g. `visualEnabled`,
 * concrete delivery thresholds) are ADDED here and defaulted in `defaults`;
 * the data layer spreads stored channel objects before normalizing known
 * fields, so unknown keys already round-trip losslessly.
 *
 * #233 took that extension: `visualEnabled` + `deliveryThreshold` on the
 * settings, `threshold` capability metadata on the meta, a nullable
 * `legacyKey`, and the two visual-only velocity channels.
 *
 * ### Row sources for #232
 *
 * `ACTIVE_ALERT_CHANNEL_IDS` is "non-dormant" and now includes two channels
 * with NO tone. Any surface with a tone preview or a volume slider must build
 * its rows from `SOUND_ALERT_CHANNEL_IDS`; flash toggles use
 * `VISUAL_ALERT_CHANNEL_IDS`. Using the active list for previews renders dead
 * buttons on the velocity rows.
 */

import type { DeliveryThresholdMeta } from './deliveryThresholds';

export type AlertCategory = 'fills' | 'orders' | 'queue' | 'divergence' | 'system' | 'risk';

export type AlertChannelId =
  | 'fill'
  | 'firstFill'
  | 'whaleFill'
  | 'botFill'
  | 'orderPlaced'
  | 'orderAck'
  | 'orderRejected'
  | 'boot'
  | 'queueWarning'
  | 'queueCritical'
  | 'fellBehind'
  | 'velocityWarning'
  | 'velocityCritical'
  | 'divergence'
  | 'divergenceLiveGame'
  | 'defense'
  | 'sweep'
  | 'exposureBreach'
  | 'adverseFlow'
  | 'bestPrice';

/** Legacy flat boolean keys in the `galactus:soundSettings` payload. */
export type LegacySoundToggleKey =
  | 'tradeSoundEnabled'
  | 'firstSaleSoundEnabled'
  | 'whaleSoundEnabled'
  | 'botFillSoundEnabled'
  | 'orderPlacedSoundEnabled'
  | 'orderRejectionSoundEnabled'
  | 'bootSoundEnabled'
  | 'queueFrontSoundEnabled'
  | 'divergenceSoundEnabled'
  | 'divergenceLiveGameSoundEnabled'
  | 'defenseSoundEnabled'
  | 'sweepSoundEnabled'
  | 'exposureBreachSoundEnabled'
  | 'adverseFlowSoundEnabled'
  | 'bestPriceSoundEnabled';

export interface AlertChannelSettings {
  soundEnabled: boolean;
  /**
   * Whether the channel's flash/visual cue is delivered. Independent of
   * `soundEnabled`: muting one must not suppress the other, nor stop its
   * edge/latch state advancing. Always false where `meta.visual` is false.
   */
  visualEnabled: boolean;
  /** Per-channel multiplier on master volume. 1.0 = today's behavior. */
  volume: number;
  /**
   * The user's delivery threshold in `meta.threshold.unit`, or null where the
   * channel declares no tunable threshold. This gates DELIVERY only — it never
   * feeds a queue, price, velocity, or accounting measurement.
   */
  deliveryThreshold: number | null;
}

export interface AlertChannelMeta {
  id: AlertChannelId;
  label: string;
  description: string;
  category: AlertCategory;
  /** Has an audible tone wired to a production emit site today. */
  sound: boolean;
  /** Has a visual affordance today (flash, badge). */
  visual: boolean;
  /**
   * Preserved for storage compatibility but never advertised: no production
   * emit site exists. The Settings UI must not render dormant channels.
   */
  dormant: boolean;
  /**
   * The legacy flat boolean this channel migrates from. The three queue
   * channels all map from `queueFrontSoundEnabled` (deterministic fan-out).
   *
   * NULL for channels with no legacy flat boolean — the two velocity channels
   * are new in #233 and have nothing to migrate from or mirror to.
   */
  legacyKey: LegacySoundToggleKey | null;
  /**
   * Renderable bounds for the channel's delivery threshold, or null when the
   * channel has none. Capability metadata, so #232 can build the right input
   * without inventing values.
   */
  threshold: DeliveryThresholdMeta | null;
  defaults: Readonly<AlertChannelSettings>;
}

/** Sound-only channel: audible by default, no flash, no tunable threshold. */
const ENABLED: Readonly<AlertChannelSettings> = Object.freeze({
  soundEnabled: true,
  visualEnabled: false,
  volume: 1.0,
  deliveryThreshold: null,
});
const DISABLED: Readonly<AlertChannelSettings> = Object.freeze({
  soundEnabled: false,
  visualEnabled: false,
  volume: 1.0,
  deliveryThreshold: null,
});

/**
 * Shared threshold bounds. Frozen because `ALERT_REGISTRY` entries are frozen
 * and #232 imports them; a mutated `max` would desync every consumer's input.
 */
const CONTRACTS_BOUNDS: DeliveryThresholdMeta = Object.freeze({
  unit: 'contracts',
  min: 0,
  max: 1_000_000,
  step: 1_000,
});
const PER_SECOND_BOUNDS: DeliveryThresholdMeta = Object.freeze({
  unit: 'contractsPerSecond',
  min: 0.1,
  max: 200,
  step: 0.1,
});
/**
 * Minimum 2¢, not 1.5¢. The personal visual rearm floor is
 * `max(DIVERGENCE_REARM_CENTS, threshold - 0.5)`; at a 1.5¢ threshold
 * activation (>= 1.5) and rearm (< 1.5) would be adjacent and a value
 * oscillating on the boundary would fire every tick.
 */
const CENTS_BOUNDS: DeliveryThresholdMeta = Object.freeze({
  unit: 'cents',
  min: 2,
  max: 25,
  step: 0.5,
});

/** A channel that delivers a flash and/or a tone at a tunable threshold. */
function deliverySettings(opts: {
  soundEnabled: boolean;
  visualEnabled: boolean;
  deliveryThreshold: number;
}): Readonly<AlertChannelSettings> {
  return Object.freeze({ ...opts, volume: 1.0 });
}

/**
 * Entries are frozen individually as well as the map — this is the shared
 * contract #232/#233 import, so a consumer must not be able to mutate a
 * channel's label, legacyKey, or dormant flag out from under everyone.
 */
const REGISTRY_ENTRIES: Record<AlertChannelId, AlertChannelMeta> = {
  fill: {
    id: 'fill',
    label: 'Fill',
    // "A resting order fills" was wrong in the one case that matters: the FIRST
    // fill of an order rings the ka-ching instead, so this tone is specifically
    // the later ones. `accountStore.processFill` picks it only when neither the
    // whale nor the first-fill branch takes the fill.
    description: 'An order fills — the standard tone, after that order’s first fill.',
    category: 'fills',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'tradeSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  firstFill: {
    id: 'firstFill',
    label: 'First fill (ka-ching)',
    // Per ORDER, not per session: `accountStore.processFill` keys first-sight on
    // `fill.orderId` (`_seenOrderIds`), cleared only on disconnect.
    description: 'The first fill of an order — later fills of it use the standard tone.',
    category: 'fills',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'firstSaleSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  whaleFill: {
    id: 'whaleFill',
    label: 'Whale fill',
    // The whale size is the hardcoded `fill.count >= 1000` in
    // `accountStore.processFill` — not a tunable threshold, so it is stated here.
    description: 'A single fill of 1,000 contracts or more.',
    category: 'fills',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'whaleSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  botFill: {
    id: 'botFill',
    label: 'Penelope fills',
    // A GATE on the fill tones, not a tone of its own (Doom 10-17): a fill the relay tags as a bot order's (Penelope,
    // the harvester, the mini game — `fill.bot`) rings the usual fill / first-fill / whale tone only while this is on.
    // Off: her fills on your account stay silent; your own fills ring as before.
    description: 'A fill of a Penelope order on your account (the relay tags them). Off: her fills stay silent; your own fills still ring.',
    category: 'fills',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'botFillSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  orderPlaced: {
    id: 'orderPlaced',
    label: 'Order placed',
    description: 'An order is accepted by the venue.',
    category: 'orders',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'orderPlacedSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  orderAck: {
    id: 'orderAck',
    label: 'Order acknowledged',
    description:
      'Your input passed the local checks and the order request is on its way — rings before the venue answers, so it is never venue truth.',
    category: 'orders',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: null,
    threshold: null,
    defaults: ENABLED,
  },
  orderRejected: {
    id: 'orderRejected',
    label: 'Order rejected',
    description: 'An order is refused — locally by the guard, or by the venue.',
    category: 'orders',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'orderRejectionSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  boot: {
    id: 'boot',
    label: 'Boot',
    // NOT a reconnect chime: `useBootSound` is a ref-guarded mount effect with
    // an empty dep array, so it fires once per page load. A relay/websocket
    // reconnect never remounts App and never rings this.
    description: 'Plays once when the app loads.',
    category: 'system',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'bootSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  queueWarning: {
    id: 'queueWarning',
    label: 'Queue warning',
    description: 'Queue ahead of a resting order crosses down through the warning level.',
    category: 'queue',
    sound: true,
    // Flips false -> true in #233: the opt-in warning flash now exists. The
    // DEFAULT stays off, so no existing user gains a new flash.
    visual: true,
    dormant: false,
    legacyKey: 'queueFrontSoundEnabled',
    threshold: CONTRACTS_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: true,
      visualEnabled: false,
      deliveryThreshold: 50_000,
    }),
  },
  queueCritical: {
    id: 'queueCritical',
    label: 'Queue critical',
    description: 'Queue ahead of a resting order crosses down through the critical level.',
    category: 'queue',
    sound: true,
    visual: true,
    dormant: false,
    legacyKey: 'queueFrontSoundEnabled',
    threshold: CONTRACTS_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: true,
      visualEnabled: true,
      deliveryThreshold: 10_000,
    }),
  },
  fellBehind: {
    id: 'fellBehind',
    label: 'Fell behind / new level above',
    description: 'Better-priced size forms or grows way ahead of a resting order.',
    category: 'queue',
    sound: true,
    visual: true,
    dormant: false,
    legacyKey: 'queueFrontSoundEnabled',
    threshold: CONTRACTS_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: true,
      visualEnabled: true,
      deliveryThreshold: 200_000,
    }),
  },
  velocityWarning: {
    id: 'velocityWarning',
    label: 'Queue velocity warning',
    description: 'Queue ahead of a resting order drains at the warning rate.',
    category: 'queue',
    // The first ACTIVE channels with no tone. There is no velocity chime
    // today and #233 does not add one — these are flash-only.
    sound: false,
    visual: true,
    dormant: false,
    legacyKey: null,
    threshold: PER_SECOND_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: false,
      visualEnabled: true,
      deliveryThreshold: 5,
    }),
  },
  velocityCritical: {
    id: 'velocityCritical',
    label: 'Queue velocity critical',
    description: 'Queue ahead of a resting order drains at the critical rate.',
    category: 'queue',
    sound: false,
    visual: true,
    dormant: false,
    legacyKey: null,
    threshold: PER_SECOND_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: false,
      visualEnabled: true,
      deliveryThreshold: 20,
    }),
  },
  divergence: {
    id: 'divergence',
    label: 'Cross-venue divergence',
    description: 'Polymarket mid diverges from Kalshi on a market you hold an order/position in.',
    category: 'divergence',
    sound: true,
    visual: true,
    dormant: false,
    legacyKey: 'divergenceSoundEnabled',
    threshold: CENTS_BOUNDS,
    defaults: deliverySettings({
      soundEnabled: true,
      visualEnabled: true,
      deliveryThreshold: 2,
    }),
  },
  divergenceLiveGame: {
    id: 'divergenceLiveGame',
    label: 'Divergence during live games',
    description:
      'Also ring the divergence chime for games already in progress (the flash/badge is unaffected).',
    category: 'divergence',
    sound: true,
    visual: false,
    dormant: false,
    legacyKey: 'divergenceLiveGameSoundEnabled',
    threshold: null,
    defaults: DISABLED,
  },
  defense: {
    id: 'defense',
    label: 'Defense',
    description: 'Dormant defense tones — no production emit site.',
    category: 'risk',
    sound: false,
    visual: false,
    dormant: true,
    legacyKey: 'defenseSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  sweep: {
    id: 'sweep',
    label: 'Sweep',
    description: 'Dormant sweep tone — no production emit site.',
    category: 'risk',
    sound: false,
    visual: false,
    dormant: true,
    legacyKey: 'sweepSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  exposureBreach: {
    id: 'exposureBreach',
    label: 'Exposure breach',
    description: 'Dormant exposure-breach tone — no production emit site.',
    category: 'risk',
    sound: false,
    visual: false,
    dormant: true,
    legacyKey: 'exposureBreachSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  adverseFlow: {
    id: 'adverseFlow',
    label: 'Adverse flow',
    description: 'Dormant adverse-flow tone — no production emit site.',
    category: 'risk',
    sound: false,
    visual: false,
    dormant: true,
    legacyKey: 'adverseFlowSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
  bestPrice: {
    id: 'bestPrice',
    label: 'Best price',
    description: 'Dormant best-price tone — no production emit site.',
    category: 'queue',
    sound: false,
    visual: false,
    dormant: true,
    legacyKey: 'bestPriceSoundEnabled',
    threshold: null,
    defaults: ENABLED,
  },
};

export const ALERT_REGISTRY: Readonly<Record<AlertChannelId, AlertChannelMeta>> = Object.freeze(
  Object.fromEntries(
    Object.entries(REGISTRY_ENTRIES).map(([id, meta]) => [id, Object.freeze(meta)])
  ) as Record<AlertChannelId, AlertChannelMeta>
);

/** Ordered, stable ID list. Order is the #232 Settings row order. */
export const ALERT_CHANNEL_IDS: readonly AlertChannelId[] = Object.freeze([
  'fill',
  'firstFill',
  'whaleFill',
  'botFill',
  'orderPlaced',
  'orderAck',
  'orderRejected',
  'boot',
  'queueWarning',
  'queueCritical',
  'fellBehind',
  'velocityWarning',
  'velocityCritical',
  'divergence',
  'divergenceLiveGame',
  'defense',
  'sweep',
  'exposureBreach',
  'adverseFlow',
  'bestPrice',
]);

/** Channels the product actually emits today. */
export const ACTIVE_ALERT_CHANNEL_IDS: readonly AlertChannelId[] = Object.freeze(
  ALERT_CHANNEL_IDS.filter((id) => !ALERT_REGISTRY[id].dormant)
);

/**
 * Active AND audible — the REQUIRED row source for any #232 surface with a
 * tone preview or a volume slider. `ACTIVE_ALERT_CHANNEL_IDS` is no longer
 * safe for that: since #233 it contains the two velocity channels, which have
 * no tone to preview and no volume to scale.
 */
export const SOUND_ALERT_CHANNEL_IDS: readonly AlertChannelId[] = Object.freeze(
  ACTIVE_ALERT_CHANNEL_IDS.filter((id) => ALERT_REGISTRY[id].sound)
);

/** Active AND flashing — the row source for #232's flash toggles. */
export const VISUAL_ALERT_CHANNEL_IDS: readonly AlertChannelId[] = Object.freeze(
  ACTIVE_ALERT_CHANNEL_IDS.filter((id) => ALERT_REGISTRY[id].visual)
);

/**
 * Active AND tunable — the row source for #234's Position Tracker pull-up.
 *
 * A channel belongs to that surface exactly when it declares a personal
 * delivery threshold, so a new tunable channel appears there with no edit to
 * the pull-up. Deriving on `threshold` rather than enumerating also excludes
 * `divergenceLiveGame` for the right reason: it is a sound-only GATE on the
 * divergence chime with nothing of its own to tune, and it stays in Settings.
 */
export const THRESHOLD_ALERT_CHANNEL_IDS: readonly AlertChannelId[] = Object.freeze(
  ACTIVE_ALERT_CHANNEL_IDS.filter((id) => ALERT_REGISTRY[id].threshold !== null)
);
